import { RBCGroupedCategory, RBCCellTypeStats } from "../types/rbc";
import { RBCDashboardResponse } from "../types/api";
import { RBC_CELL_TYPE_TREE, CellTypeMenuOption } from "./constants/cellTypeMenuConfig";

const NEAR_COMPLETION_THRESHOLD = 95;
const COMPLETED_THRESHOLD = 100;

/**
 * Determines the status of a category based on current count and progress.
 */
function determineStatus(count: number, target: number, progress: number): RBCCellTypeStats["status"] {
  if (count >= target || progress >= COMPLETED_THRESHOLD) {
    return "completed";
  }
  if (progress >= NEAR_COMPLETION_THRESHOLD) {
    return "near-completion";
  }
  return "in-progress";
}

/**
 * Maps flat API statistics to a grouped hierarchy based on the project configuration.
 * 
 * @param data - The raw dashboard response from the API.
 * @param config - The hierarchical configuration for RBC categories.
 * @returns A structured list of grouped categories with progress metrics.
 */
export function groupDashboardData(
  data: RBCDashboardResponse,
  config: typeof RBC_CELL_TYPE_TREE = RBC_CELL_TYPE_TREE
): RBCGroupedCategory[] {
  const target = data.target_per_category || 10000;
  // Standardize backend keys to lowercase for resilient matching
  const stats = normalizeKeys(data.category_total || {});

  return config.map((group) => {
    // Some groups are direct items (leaf nodes), others represent parent categories with children.
    const children = group.children || [group];

    const items: RBCCellTypeStats[] = children.map((child: CellTypeMenuOption) => {
      // Perform case-insensitive lookup using both label and name
      const count = stats[child.label?.toLowerCase() || ""] ?? 0;
      const rawProgress = (count / target) * 100;
      const progress = Math.min(Math.floor(rawProgress), 100);

      return {
        label: child.label || child.name,
        name: child.name,
        count,
        target,
        progress,
        status: determineStatus(count, target, progress),
      };
    });

    return {
      label: group.label || group.name,
      name: group.name,
      items,
    };
  });
}

/**
 * Returns the visual theme configuration for a given progress percentage and status.
 * Maps logical status to CSS classes and human-readable labels.
 * 
 * @param progress - Numeric progress percentage (0-100).
 * @param status - The logical status of the category.
 */
export function getStatusTheme(progress: number, status: RBCCellTypeStats["status"]) {
  if (status === "completed" || progress >= COMPLETED_THRESHOLD) {
    return {
      label: "Completed",
      colorClass: "status-success",
      badgeClass: "status-badge-success",
      rowClass: "row-completed",
    };
  }
  if (status === "near-completion" || progress >= NEAR_COMPLETION_THRESHOLD) {
    return {
      label: "Near Completion",
      colorClass: "status-warning",
      badgeClass: "status-badge-warning",
      rowClass: "row-near-completion",
    };
  }
  return {
    label: "In Progress",
    colorClass: "status-danger",
    badgeClass: "status-badge-danger",
    rowClass: "row-in-progress",
  };
}

/**
 * Returns a CSS color class based on progress percentage.
 * @deprecated Use getStatusTheme instead.
 */
export function getProgressColorClass(progress: number): string {
  return getStatusTheme(progress, "in-progress").colorClass;
}

/**
 * Returns a human-readable status label.
 * @deprecated Use getStatusTheme instead.
 */
export function getStatusLabel(status: RBCCellTypeStats["status"]): string {
  const theme = getStatusTheme(0, status);
  return theme.label;
}
/**
 * Normalizes an object's keys to lowercase for case-insensitive lookup.
 * In case of duplicate keys (after normalization), the last one wins.
 */
function normalizeKeys<T>(obj: Record<string, T>): Record<string, T> {
  const result: Record<string, T> = {};
  Object.keys(obj).forEach((key) => {
    result[key.toLowerCase()] = obj[key];
  });
  return result;
}
